import { Button, Card, Field, Input, Notice, Option, Page, Progress, Spinner, Textarea } from '@/components/ui';
import { track } from '@/lib/analytics';
import { ApiRequestError, lookupCode, redeemGift } from '@/lib/api';
import { FIRST_STEP_ID, finalizeAnswers, getStep, nextStepId, progressFor, type QuizStep } from '@/redeem/questions';
import { GIFT_TERM_LABEL, type CodeLookupResponse } from '@shared/types';
import type { JSX } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';

type Phase = 'code' | 'intro' | 'quiz' | 'email' | 'name' | 'submitting' | 'done';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const NAME_RE = /^[\p{L}\p{N}]+(?:[ '’.-]+[\p{L}\p{N}]+)*\.?$/u;

const CODE_ERRORS: Record<string, string> = {
  not_found: "We couldn't find that code. Check the email you received and try again.",
  not_paid: "This gift hasn't been paid for yet. Ask the person who sent it to check their payment.",
  already_redeemed: 'This gift code has already been redeemed. If that was you, open the Betteroo app and sign in with your email.',
  refunded: 'This gift was refunded and can no longer be redeemed.',
  expired: 'This gift code has expired. Please contact support@betteroo.ai.',
};

export function Redeem(): JSX.Element {
  const initialCode = new URLSearchParams(window.location.search).get('code') ?? '';
  const [codeInput, setCodeInput] = useState(initialCode);
  const [code, setCode] = useState(initialCode);
  const [gift, setGift] = useState<CodeLookupResponse | null>(null);
  const [checking, setChecking] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  const [phase, setPhase] = useState<Phase>('code');
  const [stepId, setStepId] = useState(FIRST_STEP_ID);
  const [history, setHistory] = useState<string[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState<string>('');

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Validate a code coming from the link automatically.
  useEffect(() => {
    track('Gift Redeem Started', { hasCode: !!initialCode });
    if (initialCode) void check(initialCode);
  }, []);

  async function check(c: string): Promise<void> {
    const trimmed = c.trim();
    if (!trimmed) {
      setCodeError('Please enter your gift code');
      return;
    }
    setChecking(true);
    setCodeError(null);
    try {
      const res = await lookupCode(trimmed);
      if (res.status !== 'valid') {
        setCodeError(CODE_ERRORS[res.status] ?? 'This code cannot be redeemed.');
        setGift(null);
        return;
      }
      setGift(res);
      setCode(trimmed);
      setEmail(res.recipientEmail ?? '');
      setFirstName(res.recipientName ?? '');
      setPhase('intro');
      track('Gift Code Validated', { term: res.term ?? null });
    } catch (e) {
      setCodeError(e instanceof Error ? e.message : 'Could not check the code. Please try again.');
    } finally {
      setChecking(false);
    }
  }

  const step: QuizStep | undefined = useMemo(() => getStep(stepId), [stepId]);

  function goTo(next: string | null): void {
    if (next === null) {
      setPhase('email');
      return;
    }
    setHistory((h) => [...h, stepId]);
    setStepId(next);
    setDraft(answers[next] ?? '');
  }

  function back(): void {
    if (phase === 'email') {
      setPhase('quiz');
      return;
    }
    if (phase === 'name') {
      setPhase('email');
      return;
    }
    const prev = history[history.length - 1];
    if (!prev) {
      setPhase('intro');
      return;
    }
    setHistory((h) => h.slice(0, -1));
    setStepId(prev);
    setDraft(answers[prev] ?? '');
  }

  function answerStep(value: string): void {
    if (!step) return;
    const next = { ...answers, [step.id]: value };
    setAnswers(next);
    goTo(nextStepId(step, value));
  }

  async function submit(): Promise<void> {
    const name = firstName.trim();
    if (!name || name.length > 30 || !NAME_RE.test(name)) {
      setFieldError('Please enter your first name (letters only, up to 30 characters)');
      return;
    }
    setFieldError(null);
    setSubmitError(null);
    setPhase('submitting');
    try {
      const res = await redeemGift({ code, email: email.trim(), firstName: name, quizAnswers: finalizeAnswers(answers) });
      track('Gift Redeemed', { deferred: res.deferred });
      setPhase('done');
      window.location.href = res.appUrl;
    } catch (e) {
      setPhase('name');
      setSubmitError(e instanceof ApiRequestError ? e.message : 'Something went wrong. Please try again.');
    }
  }

  // ---------- render ----------

  if (phase === 'code') {
    return (
      <Page>
        <Card className="space-y-4">
          <h1 className="font-heading text-2xl font-bold">Redeem your Betteroo gift</h1>
          <p className="text-gray-700">Enter the gift code from your email. It looks like <span className="font-mono">BTR-XXXX-XXXX</span>.</p>
          <Field label="Gift code" htmlFor="code" error={codeError}>
            <Input
              id="code"
              value={codeInput}
              placeholder="BTR-7K3M-9QWD"
              autocomplete="off"
              autocapitalize="characters"
              spellcheck={false}
              className="font-mono tracking-wider uppercase"
              onInput={(e) => setCodeInput((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void check(codeInput);
              }}
              invalid={!!codeError}
            />
          </Field>
          <Button onClick={() => void check(codeInput)} loading={checking}>
            Continue
          </Button>
        </Card>
      </Page>
    );
  }

  if (phase === 'intro' && gift) {
    return (
      <Page>
        <Card className="space-y-4 text-center">
          <div className="text-5xl">🎁</div>
          <h1 className="font-heading text-2xl font-bold">
            {gift.recipientName}, {gift.gifterName} gifted you {GIFT_TERM_LABEL[gift.term ?? '3m']} of Betteroo
          </h1>
          {gift.message ? (
            <blockquote className="rounded-xl bg-brand-beige p-4 text-left italic text-gray-700">
              “{gift.message}”<span className="mt-1 block not-italic text-sm text-gray-500">— {gift.gifterName}</span>
            </blockquote>
          ) : null}
          <p className="text-gray-700">
            A sleep plan built around your child, daily guidance and a real human sleep expert. Nothing to pay, ever.
            Answer a few quick questions so we can build your plan.
          </p>
          <Button onClick={() => { setPhase('quiz'); setDraft(answers[FIRST_STEP_ID] ?? ''); }} variant="green">
            Let's start
          </Button>
        </Card>
      </Page>
    );
  }

  if (phase === 'quiz' && step) {
    const selectedMulti = draft ? draft.split(',').filter(Boolean) : [];
    return (
      <Page>
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <button type="button" onClick={back} className="text-sm text-gray-500 hover:text-gray-800" aria-label="Back">
              ← Back
            </button>
            <div className="flex-1"><Progress value={progressFor(step.id)} /></div>
          </div>
          <h1 className="font-heading text-2xl font-bold">{step.title}</h1>
          {step.subtitle ? <p className="-mt-3 text-gray-600">{step.subtitle}</p> : null}

          {step.type === 'single' ? (
            <div className="space-y-2">
              {step.options.map((o) => (
                <Option key={o.id} id={o.id} name={step.id} label={o.label} emoji={o.emoji} selected={answers[step.id] === o.id} onSelect={() => answerStep(o.id)} />
              ))}
            </div>
          ) : null}

          {step.type === 'multi' ? (
            <>
              <div className="space-y-2">
                {step.options.map((o) => (
                  <Option
                    key={o.id}
                    id={o.id}
                    name={step.id}
                    label={o.label}
                    emoji={o.emoji}
                    multi
                    selected={selectedMulti.includes(o.id)}
                    onSelect={() => {
                      const next = selectedMulti.includes(o.id) ? selectedMulti.filter((x) => x !== o.id) : [...selectedMulti, o.id];
                      setDraft(next.join(','));
                    }}
                  />
                ))}
              </div>
              <Button onClick={() => answerStep(draft)} disabled={selectedMulti.length === 0}>Continue</Button>
            </>
          ) : null}

          {step.type === 'date' ? (
            <>
              <Input type="date" value={draft} max={new Date().toISOString().slice(0, 10)} onInput={(e) => setDraft((e.target as HTMLInputElement).value)} />
              <Button onClick={() => answerStep(draft)} disabled={!/^\d{4}-\d{2}-\d{2}$/.test(draft) || draft > new Date().toISOString().slice(0, 10)}>Continue</Button>
            </>
          ) : null}

          {step.type === 'textarea' ? (
            <>
              <Textarea rows={4} maxLength={step.max} placeholder={step.placeholder} value={draft} onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)} />
              <Button onClick={() => answerStep(draft.trim())}>{draft.trim() ? 'Continue' : 'Skip'}</Button>
            </>
          ) : null}
        </div>
      </Page>
    );
  }

  if (phase === 'email' || phase === 'name' || phase === 'submitting') {
    const isEmail = phase === 'email';
    return (
      <Page>
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <button type="button" onClick={back} className="text-sm text-gray-500 hover:text-gray-800" aria-label="Back" disabled={phase === 'submitting'}>
              ← Back
            </button>
            <div className="flex-1"><Progress value={isEmail ? 92 : 100} /></div>
          </div>
          {isEmail ? (
            <>
              <h1 className="font-heading text-2xl font-bold">Which email should we set up your account with?</h1>
              <p className="-mt-3 text-gray-600">You'll use it to sign in to Betteroo.</p>
              <Field label="Email" htmlFor="email" error={fieldError}>
                <Input id="email" type="email" inputMode="email" autocomplete="email" value={email} onInput={(e) => setEmail((e.target as HTMLInputElement).value)} invalid={!!fieldError} />
              </Field>
              <Button
                variant="green"
                onClick={() => {
                  if (!EMAIL_RE.test(email.trim())) {
                    setFieldError('Please enter a valid email');
                    return;
                  }
                  setFieldError(null);
                  setPhase('name');
                }}
              >
                Continue
              </Button>
              <p className="text-center text-xs text-gray-500">We respect your privacy and never share your data.</p>
            </>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold">What's your first name?</h1>
              <Field label="First name" htmlFor="firstName" error={fieldError}>
                <Input id="firstName" autocomplete="given-name" maxLength={30} value={firstName} onInput={(e) => setFirstName((e.target as HTMLInputElement).value)} invalid={!!fieldError} />
              </Field>
              {submitError ? <Notice tone="error">{submitError}</Notice> : null}
              <Button variant="green" onClick={() => void submit()} loading={phase === 'submitting'}>
                Start my Betteroo
              </Button>
              <p className="text-center text-xs text-gray-500">
                By continuing you agree to our <a href="https://betteroo.ai/terms-of-service/" className="underline">terms</a> and{' '}
                <a href="https://betteroo.ai/privacy-policy/" className="underline">privacy policy</a>.
              </p>
            </>
          )}
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <Card className="flex flex-col items-center gap-3 py-8 text-gray-600">
        <Spinner />
        <p>Taking you to your Betteroo…</p>
      </Card>
    </Page>
  );
}
